import { SagaIterator } from 'redux-saga';
import { call, cancel, put, race, select, take, takeEvery } from 'redux-saga/effects';
import {
  CHANGE_SEARCH_DIALOG_STATE,
  changeSearchDialogState,
  SearchDialogContainerStateChangeAction,
} from '@ps20-frontend/gui-components';
import { getEndpointUrl, GlobalActions, GlobalPut } from '@ps20-frontend/shop-integration-redux';
import { PersonensucheRequest, PersonensucheResponse, SearchFilter } from '@ps20/bff-models';
import {
  App,
  ConfirmationState,
  mapAmisCrmResponseToPersonenSearchList,
  PersonenSearchListItem,
  PersonensucheModal,
  PersonenSucheStatus,
  PersonenSucheStatusCell,
  SearchlistItem,
} from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { CheckoutPersonenSearchListItem } from '../model/checkoutPersonenSearchListItem';
import { CheckoutActions } from '../checkout.actions';
import { IAppState } from '../localStore';
import { mapCrmBankverbindungenToBankverbindung } from '../reducer/zahlung.reducer';
import { selectPersonenSuchdialog, shouldUseSdwEndpoint } from '../selector/transient-state.selector';

const url = '/privatschutz/api/personensuche';

export enum PERSONEN_SUCHE_EVENTS {
  SUCHEN_SUCCESS = 'PERSONEN_SUCHE_SUCCESS',
  SUCHEN_FAILED = 'PERSONEN_SUCHE_FAILED',
}

export function* watchPersonenSucheTrigger(): SagaIterator {
  yield takeEvery(CHANGE_SEARCH_DIALOG_STATE, safeSaga(requestPersonenSuche, App.SHOP_CHECKOUT));
  yield takeEvery(CHANGE_SEARCH_DIALOG_STATE, safeSaga(validateSelectedItem, App.SHOP_CHECKOUT));
  yield takeEvery(CHANGE_SEARCH_DIALOG_STATE, safeSaga(confirmPerson, App.SHOP_CHECKOUT));
  yield takeEvery(
    PERSONEN_SUCHE_EVENTS.SUCHEN_SUCCESS,
    safeSaga(successCallback, App.SHOP_CHECKOUT),
  );
  yield takeEvery(PERSONEN_SUCHE_EVENTS.SUCHEN_FAILED, safeSaga(errorCallback, App.SHOP_CHECKOUT));
}

export function* requestPersonenSuche(
  action: SearchDialogContainerStateChangeAction<SearchlistItem>,
): SagaIterator {
  if (
    action.payload.searchString === undefined &&
    action.payload.exactNameSearchString === undefined
  ) {
    return yield cancel();
  }

  yield put(
    changeSearchDialogState({
      loading: true,
      error: false,
      items: [],
      selectedItem: null,
    }),
  );

  const state: IAppState = yield select();
  const request: PersonensucheRequest = {
    firstName: action.payload.exactNameSearchString?.firstName,
    lastName: action.payload.exactNameSearchString?.lastName,
    searchPattern: action.payload.searchString,
    searchFilters:
      selectPersonenSuchdialog(state).personensucheModal === PersonensucheModal.BEZUGSRECHT
        ? [SearchFilter.NAT_PERS_ONLY]
        : undefined,
  };

  const globalPut = GlobalPut.getInstance();
  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().doRequest({
      appId: App.SHOP_CHECKOUT,
      body: request,
      url: getEndpointUrl(shouldUseSdwEndpoint(state), url),
      successEvent: PERSONEN_SUCHE_EVENTS.SUCHEN_SUCCESS,
      errorEvent: PERSONEN_SUCHE_EVENTS.SUCHEN_FAILED,
      noCache: true,
    }),
  );
}

function* changeKontoinhaber(selectedPerson: CheckoutPersonenSearchListItem['personData']): SagaIterator {
  yield put(
    new CheckoutActions().changeKontoinhaber({
      kundenverwaltungID: selectedPerson.kundenverwaltungID,
      vorname: selectedPerson.vorname,
      nachname: selectedPerson.nachname,
      firmenname: selectedPerson.firmenname,
      juristisch: selectedPerson.juristisch,
      businessForm: selectedPerson.businessForm,
      adelsbez: selectedPerson.adelsbez,
      adelstitel: selectedPerson.adelstitel,
      adresse: selectedPerson.adresse,
      akadgrad: selectedPerson.akadgrad,
      anrede: selectedPerson.anrede,
      bankverbindungen: selectedPerson.bankverbindungen,
      famstand: selectedPerson.famstand,
      geburtsdatum: selectedPerson.geburtsdatum,
      geburtsland: selectedPerson.geburtsland,
      heimatort: selectedPerson.heimatort,
      kundennummer: selectedPerson.kundennummer ? selectedPerson.kundennummer : '',
      namenszusatz: selectedPerson.namenszusatz,
      staatsbschaft: selectedPerson.staatsbschaft,
    }),
  );
  const bankverbindungen = selectedPerson.bankverbindungen;
  const iban = bankverbindungen && bankverbindungen[0] && bankverbindungen[0].iban;
  if (iban) {
    yield put(new CheckoutActions().manuelleIbanEingabe(false));
    yield put(new CheckoutActions().ibanChanged(iban));
  } else {
    yield put(new CheckoutActions().manuelleIbanEingabe(true));
  }

  yield put(changeSearchDialogState({ loading: false, error: false, open: false }));
}

function* addBezugsberechtigter(selectedPerson: CheckoutPersonenSearchListItem['personData']): SagaIterator {
  const state: IAppState = yield select();
  const angebotsId = selectPersonenSuchdialog(state).angebotsId;
  yield put(
    new CheckoutActions().unfAddBezugsberechtigter({
      person: {
        kdvId: selectedPerson.kundenverwaltungID,
        vorname: selectedPerson.vorname,
        nachname: selectedPerson.nachname,
        geburtsdatum: selectedPerson.geburtsdatum,
        anteil: 0,
        anrede: selectedPerson.anrede,
        adelsbez: selectedPerson.adelsbez,
        adelstitel: selectedPerson.adelstitel,
        adresse: selectedPerson.adresse,
        akadgrad: selectedPerson.akadgrad,
      },
      angebotsId,
    }),
  );
  yield put(changeSearchDialogState({ loading: false, error: false, open: false }));
}

function* checkPersonensuchDialogCanceled(): SagaIterator {
  const appState: IAppState = yield select();
  return !appState.transientState.personenSuchdialog.open;
}

export function* validateSelectedItem(
  action: SearchDialogContainerStateChangeAction<CheckoutPersonenSearchListItem>,
): SagaIterator {
  if (action.payload.selectedItem == null) {
    return yield cancel();
  }

  yield put(changeSearchDialogState({ confirmation: ConfirmationState.IN_PROGRESS }));

  const state: IAppState = yield select();
  const selectedItemPayload = state.transientState.personenSuchdialog.selectedItem.personData;

  const adresseValid = yield selectedItemPayload.adresse &&
    call(
      checkAdress,
      selectedItemPayload.adresse.plz,
      selectedItemPayload.adresse.ort,
      selectedItemPayload.adresse.strasse,
      selectedItemPayload.adresse.hausnummer,
    );
  const personensuchDialogCanceld = yield* checkPersonensuchDialogCanceled();

  if (!adresseValid && !personensuchDialogCanceld) {
    yield call(markPersonsAdressAsInvalid, state.transientState.personenSuchdialog.selectedItem.id);
  }

  const validationResult: ConfirmationState = adresseValid
    ? ConfirmationState.SUCCESS
    : ConfirmationState.ERROR;

  yield put(changeSearchDialogState({ confirmation: validationResult }));
}

export function* confirmPerson(
  action: SearchDialogContainerStateChangeAction<CheckoutPersonenSearchListItem>,
): SagaIterator {
  if (action.payload.confirmed !== true) {
    return yield cancel();
  }

  const personensucheDialogCanceled = yield* checkPersonensuchDialogCanceled();

  if (!personensucheDialogCanceled) {
    const state: IAppState = yield select();
    const personensucheModal = selectPersonenSuchdialog(state).personensucheModal;
    if (personensucheModal === PersonensucheModal.KONTO_INHABER) {
      yield call(
        changeKontoinhaber,
        state.transientState.personenSuchdialog.selectedItem.personData,
      );
    } else if (personensucheModal === PersonensucheModal.BEZUGSRECHT) {
      yield call(
        addBezugsberechtigter,
        state.transientState.personenSuchdialog.selectedItem.personData,
      );
    }
  }
}

export function* checkAdress(plz: string, ortschaft: string, strasse: string, hausnummer: string): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  yield call(
    globalPut.put,
    new GlobalActions().adresseValidieren({
      plz,
      ortschaft,
      strasse,
      hausnummer,
    }),
  );
  const { success } = yield race({
    success: take(GlobalActions.ADRESSE_VALIDIEREN_SUCCESSFULL),
    fail: take(GlobalActions.ADRESSE_VALIDIEREN_FAILED),
  });

  return success != null;
}

export function* markPersonsAdressAsInvalid(id: string): SagaIterator {
  const state: IAppState = yield select();
  const updatedItems: CheckoutPersonenSearchListItem[] =
    state.transientState.personenSuchdialog.items.map(item => {
      if (item.id === id) {
        return {
          ...item,
          status: getStatuscelldataForInvalidAdress(),
        };
      } else {
        return item;
      }
    });

  yield put(changeSearchDialogState({ items: updatedItems }));
}

function getStatuscelldataForInvalidAdress(): PersonenSucheStatusCell {
  return {
    type: 'ICON',
    icon: 'exclamation-triangle',
    cssClass: 'status-color-invalid',
    suchStatus: PersonenSucheStatus.INVALID,
  };
}

export const mapAmisCrmResponseToCheckoutPersonenSearchList = (
  payload: PersonensucheResponse,
): CheckoutPersonenSearchListItem[] => {
  return payload.personen.map(person => {
    const personenSearchListItem: PersonenSearchListItem =
      mapAmisCrmResponseToPersonenSearchList(person);
    return {
      ...personenSearchListItem,
      personData: {
        ...personenSearchListItem.personData,
        bankverbindungen: mapCrmBankverbindungenToBankverbindung(person.bankverbindungen || []),
      },
    };
  });
};

export function* successCallback(response: { payload: PersonensucheResponse }): SagaIterator {
  const _response: { payload: PersonensucheResponse } = response;
  const personenSearchListItems = mapAmisCrmResponseToCheckoutPersonenSearchList(_response.payload);

  yield put(
    changeSearchDialogState({
      loading: false,
      error: false,
      items: personenSearchListItems,
    }),
  );
}

export function* errorCallback(response: { payload: { response: { data: { message: string } } } }): SagaIterator {
  yield put(
    changeSearchDialogState({
      loading: false,
      error: true,
      errorMessage: response.payload.response.data.message,
    }),
  );
}
